import { useDialogFocus } from "hooks/useDialogFocus";
import React from "react";
import { useTranslation } from "react-i18next";

import type { IconName } from "../models";

import LineIcon from "./LineIcon";

import styles from "../HomePage.module.css";

export type EntityOption = {
	description: string;
	icon: IconName;
	label: string;
	value: string;
};

type AddEntityDialogProps = {
	onClose: () => void;
	onContinue: () => void;
	onSelect: (value: string) => void;
	options: EntityOption[];
	selectedOption: string;
};

const AddEntityDialog = ({ onClose, onContinue, onSelect, options, selectedOption }: AddEntityDialogProps) => {
	const { t } = useTranslation("homePage");
	const dialogRef = useDialogFocus<HTMLDivElement>();
	const onOptionClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			onSelect(event.currentTarget.value);
		},
		[onSelect]
	);

	return (
		<div className={styles.editorModalOverlay}>
			<div
				ref={dialogRef}
				aria-labelledby="add-entity-title"
				aria-modal="true"
				className={styles.addEntityDialog}
				role="dialog">
				<div className={styles.addEntityHeader}>
					<h1 id="add-entity-title">{t("editor.addEntityDialog.title")}</h1>
					<p>{t("editor.addEntityDialog.subtitle")}</p>
				</div>

				<div className={styles.entityChoiceGrid}>
					{options.map((option: EntityOption) => {
						const isSelected = option.value === selectedOption;

						return (
							<button
								key={option.value}
								className={`${styles.entityChoiceCard} ${
									isSelected ? styles.entityChoiceCardSelected : ""
								}`}
								onClick={onOptionClick}
								type="button"
								value={option.value}>
								<span className={styles.entityChoiceIcon}>
									<LineIcon name={option.icon} />
								</span>
								<strong>{option.label}</strong>
								<span>{option.description}</span>
								{isSelected && (
									<span aria-hidden="true" className={styles.entityChoiceCheck}>
										&#10003;
									</span>
								)}
							</button>
						);
					})}
				</div>

				<div className={styles.addEntityFooter}>
					<button className={styles.addEntityCancelButton} onClick={onClose} type="button">
						{t("editor.addEntityDialog.cancel")}
					</button>
					<button className={styles.addEntityContinueButton} onClick={onContinue} type="button">
						{t("editor.addEntityDialog.continue")}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AddEntityDialog;
